// src/Components/EstadisticasAppComponent.js
// Panel de analítica propia: sesiones por plataforma (web/android/ios),
// por origen (directo / notificación), evolución diaria, usuarios más
// activos y eventos custom. Datos de nuestra tabla app_events.
import React, { useState, useEffect, useCallback } from 'react';
import './EstadisticasAppComponent.css';
import { GetAppStats } from '../Backend/ApiCalls';
import { BottomNav } from './BottomNav';

const PLATFORM_LABELS = {
  web: '🌐 Web',
  android: '🤖 Android',
  ios: '🍎 iOS',
};

const SOURCE_LABELS = {
  direct: 'Acceso directo',
  notification: 'Desde notificación',
};

const RANGES = [
  { days: 7, label: '7 días' },
  { days: 30, label: '30 días' },
  { days: 90, label: '90 días' },
  { days: 365, label: '1 año' },
];

const formatDay = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// MySQL DAYOFWEEK: 1=domingo ... 7=sábado. Lo mostramos de lunes a domingo.
const WEEKDAYS = [
  { mysql: 2, label: 'Lun' },
  { mysql: 3, label: 'Mar' },
  { mysql: 4, label: 'Mié' },
  { mysql: 5, label: 'Jue' },
  { mysql: 6, label: 'Vie' },
  { mysql: 7, label: 'Sáb' },
  { mysql: 1, label: 'Dom' },
];

// Barra horizontal proporcional (sin librerías de gráficas)
const BarRow = ({ label, count, max }) => (
  <div className="stats-bar-row">
    <span className="stats-bar-label">{label}</span>
    <div className="stats-bar-track">
      <div
        className="stats-bar-fill"
        style={{ width: `${max > 0 ? Math.max((count / max) * 100, 2) : 0}%` }}
      />
    </div>
    <span className="stats-bar-count">{count}</span>
  </div>
);

export const EstadisticasAppComponent = ({ I18n, user }) => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const loadStats = useCallback(async (rangeDays) => {
    setLoading(true);
    setError(null);
    try {
      const { response, data } = await GetAppStats(rangeDays);
      if (response.ok) setStats(data);
      else setError(data?.Error || 'Error cargando estadísticas');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats(days);
  }, [days, loadStats]);

  const maxOf = (rows, key = 'count') =>
    rows && rows.length ? Math.max(...rows.map((r) => Number(r[key]) || 0)) : 0;

  return (
    <div className="stats-app-page">
      <h1 className="titulo-page">Estadísticas de uso</h1>

      <div className="stats-range-selector">
        {RANGES.map((r) => (
          <button
            key={r.days}
            className={`stats-range-btn ${days === r.days ? 'active' : ''}`}
            onClick={() => setDays(r.days)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {loading && <p className="stats-loading">Cargando estadísticas...</p>}
      {error && <p className="stats-error">{error}</p>}

      {!loading && !error && stats && (
        <>
          {/* Totales */}
          <div className="stats-totals">
            <div className="stats-total-card">
              <span className="stats-total-number">{stats.totals.sessions}</span>
              <span className="stats-total-label">Sesiones</span>
            </div>
            <div className="stats-total-card">
              <span className="stats-total-number">{stats.totals.uniqueUsers}</span>
              <span className="stats-total-label">Usuarios únicos</span>
            </div>
            <div className="stats-total-card">
              <span className="stats-total-number">
                {stats.totals.uniqueUsers > 0
                  ? (stats.totals.sessions / stats.totals.uniqueUsers).toFixed(1)
                  : '0'}
              </span>
              <span className="stats-total-label">Sesiones por usuario</span>
            </div>
            <div className="stats-total-card">
              <span className="stats-total-number">
                {stats.totals.censusUsers > 0
                  ? `${Math.round((stats.totals.uniqueUsers / stats.totals.censusUsers) * 100)}%`
                  : '-'}
              </span>
              <span className="stats-total-label">
                Alcance del censo ({stats.totals.uniqueUsers}/{stats.totals.censusUsers})
              </span>
            </div>
          </div>

          {/* Cómo accede la gente */}
          <div className="stats-section">
            <h3>¿Desde dónde acceden?</h3>
            {stats.byPlatform.length === 0 && <p className="stats-empty">Sin datos todavía.</p>}
            {stats.byPlatform.map((row) => (
              <BarRow
                key={row.platform}
                label={PLATFORM_LABELS[row.platform] || row.platform}
                count={row.count}
                max={maxOf(stats.byPlatform)}
              />
            ))}
          </div>

          <div className="stats-section">
            <h3>¿Cómo acceden?</h3>
            {stats.bySource.length === 0 && <p className="stats-empty">Sin datos todavía.</p>}
            {stats.bySource.map((row) => (
              <BarRow
                key={row.source}
                label={SOURCE_LABELS[row.source] || row.source}
                count={row.count}
                max={maxOf(stats.bySource)}
              />
            ))}
          </div>

          {/* Qué notificaciones traen tráfico */}
          <div className="stats-section">
            <h3>Notificaciones que más aperturas generan</h3>
            {(stats.byNotification || []).length === 0 ? (
              <p className="stats-empty">
                Sin datos todavía (se registra cuando alguien entra tocando una notificación).
              </p>
            ) : (
              stats.byNotification.map((row) => (
                <BarRow
                  key={row.title}
                  label={row.title}
                  count={row.count}
                  max={maxOf(stats.byNotification)}
                />
              ))
            )}
          </div>

          {/* Horas punta */}
          <div className="stats-section">
            <h3>Horas punta</h3>
            {(stats.byHour || []).length === 0 ? (
              <p className="stats-empty">Sin datos todavía.</p>
            ) : (
              <div className="stats-daily-chart stats-hour-chart">
                {Array.from({ length: 24 }, (_, h) => {
                  const row = (stats.byHour || []).find((r) => Number(r.hour) === h);
                  const count = row ? Number(row.count) : 0;
                  const max = maxOf(stats.byHour);
                  return (
                    <div className="stats-daily-col" key={h} title={`${h}:00 — ${count} sesiones`}>
                      <div
                        className="stats-daily-bar"
                        style={{ height: `${max > 0 ? Math.max((count / max) * 100, count > 0 ? 3 : 0) : 0}%` }}
                      />
                      <span className="stats-daily-label">{h}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Día de la semana */}
          <div className="stats-section">
            <h3>Sesiones por día de la semana</h3>
            {(stats.byWeekday || []).length === 0 ? (
              <p className="stats-empty">Sin datos todavía.</p>
            ) : (
              WEEKDAYS.map((wd) => {
                const row = (stats.byWeekday || []).find((r) => Number(r.weekday) === wd.mysql);
                return (
                  <BarRow
                    key={wd.mysql}
                    label={wd.label}
                    count={row ? Number(row.count) : 0}
                    max={maxOf(stats.byWeekday)}
                  />
                );
              })
            )}
          </div>

          {/* Evolución diaria */}
          <div className="stats-section">
            <h3>Sesiones por día</h3>
            {stats.byDay.length === 0 ? (
              <p className="stats-empty">Sin datos todavía.</p>
            ) : (
              <div className="stats-daily-chart">
                {stats.byDay.map((row) => (
                  <div className="stats-daily-col" key={String(row.day)} title={`${formatDay(row.day)}: ${row.count}`}>
                    <div
                      className="stats-daily-bar"
                      style={{
                        height: `${maxOf(stats.byDay) > 0 ? Math.max((row.count / maxOf(stats.byDay)) * 100, 3) : 0}%`,
                      }}
                    />
                    <span className="stats-daily-label">{formatDay(row.day)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top usuarios */}
          <div className="stats-section">
            <h3>Usuarios más activos</h3>
            {stats.topUsers.length === 0 ? (
              <p className="stats-empty">Sin datos todavía.</p>
            ) : (
              <ol className="stats-top-users">
                {stats.topUsers.map((u) => (
                  <li key={u.userId}>
                    <span>{u.username || `Usuario #${u.userId}`}</span>
                    <span className="stats-top-count">{u.sessions} sesiones</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Usuarios sin actividad */}
          <div className="stats-section">
            <h3>
              Usuarios que no han entrado en {stats.days} días{' '}
              <span className="stats-inactive-count">({(stats.inactiveUsers || []).length})</span>
            </h3>
            {(stats.inactiveUsers || []).length === 0 ? (
              <p className="stats-empty">Todo el censo ha entrado 🎉</p>
            ) : (
              <>
                <button
                  type="button"
                  className="stats-range-btn"
                  onClick={() => setShowInactive((v) => !v)}
                >
                  {showInactive ? 'Ocultar listado' : 'Ver listado'}
                </button>
                {showInactive && (
                  <ul className="stats-inactive-list">
                    {stats.inactiveUsers.map((u) => (
                      <li key={u.Id}>{u.surname} {u.name}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Eventos custom */}
          {stats.customEvents.length > 0 && (
            <div className="stats-section">
              <h3>Otros eventos</h3>
              {stats.customEvents.map((row) => (
                <BarRow
                  key={row.eventType}
                  label={row.eventType}
                  count={row.count}
                  max={maxOf(stats.customEvents)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <BottomNav I18n={I18n} user={user} />
    </div>
  );
};
